import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import apiService from '../services/api-service';
import NavBar from './nav-bar';
import styles from './stats-dashboard.module.css';

// Register Chart.js components
Chart.register(...registerables);

/**
 * Percent reduction in mean absolute error, ML vs baseline, from whatever
 * MAE fields the API provides. '-' when the server sends no MAE (the live
 * comparison endpoint currently reports counts/retention/intervals only).
 */
export function maeImprovement(comparison) {
  const baselineMAE = comparison?.baseline?.mae ?? comparison?.baselineMAE;
  const mlMAE = comparison?.ml?.mae ?? comparison?.mlMAE;
  if (!(baselineMAE > 0) || !Number.isFinite(mlMAE)) return '-';
  const pct = (1 - mlMAE / baselineMAE) * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

export class StatsDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      comparison: null,
      progress: null,
      loading: true,
      error: null
    };

    // Chart references
    this.comparisonChartRef = React.createRef();
    this.retentionChartRef = React.createRef();
    this.intervalChartRef = React.createRef();

    this.comparisonChart = null;
    this.retentionChart = null;
    this.intervalChart = null;
  }

  async componentDidMount() {
    await this.loadData();
  }

  componentWillUnmount() {
    // Cleanup charts
    if (this.comparisonChart) this.comparisonChart.destroy();
    if (this.retentionChart) this.retentionChart.destroy();
    if (this.intervalChart) this.intervalChart.destroy();
  }

  async loadData() {
    this.setState({ loading: true, error: null });

    try {
      const [comparisonResponse, progress] = await Promise.all([
        apiService.getAlgorithmComparison(),
        apiService.getProgress()
      ]);

      // GET /questions/stats/comparison returns { comparison, mlReadiness, currentMode }
      const comparison = comparisonResponse?.comparison || comparisonResponse;

      this.setState({
        comparison,
        progress,
        loading: false
      }, () => {
        this.createCharts();
      });

    } catch (error) {
      this.setState({
        error: error.message,
        loading: false
      });
    }
  }

  createCharts() {
    this.createComparisonChart();
    this.createRetentionChart();
    this.createIntervalChart();
  }

  createComparisonChart() {
    const { comparison } = this.state;
    if (!comparison || !this.comparisonChartRef.current) return;

    const ctx = this.comparisonChartRef.current.getContext('2d');

    if (this.comparisonChart) {
      this.comparisonChart.destroy();
    }

    this.comparisonChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Baseline (SM-2)', 'ML-Enhanced'],
        datasets: [
          {
            label: 'Mean Absolute Error (days)',
            data: [
              comparison.baseline?.mae || comparison.baselineMAE || 0,
              comparison.ml?.mae || comparison.mlMAE || 0
            ],
            backgroundColor: [
              'rgba(136, 136, 136, 0.7)',
              'rgba(0, 255, 65, 0.7)'
            ],
            borderColor: [
              '#888888',
              '#00ff41'
            ],
            borderWidth: 2
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: { color: '#fff' }
          },
          title: {
            display: true,
            text: 'Prediction Accuracy Comparison',
            color: '#00ff41',
            font: { size: 18 }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { color: '#fff' },
            grid: { color: 'rgba(255, 255, 255, 0.1)' },
            title: {
              display: true,
              text: 'Error (days)',
              color: '#fff'
            }
          },
          x: {
            ticks: { color: '#fff' },
            grid: { color: 'rgba(255, 255, 255, 0.1)' }
          }
        }
      }
    });
  }

  createRetentionChart() {
    const { comparison } = this.state;
    if (!comparison || !this.retentionChartRef.current) return;

    const ctx = this.retentionChartRef.current.getContext('2d');

    if (this.retentionChart) {
      this.retentionChart.destroy();
    }

    // Mock retention curve data - in production, this would come from actual tracking
    const days = [0, 1, 3, 7, 14, 30];
    const baselineRetention = [100, 85, 70, 60, 50, 40];
    const mlRetention = [100, 88, 75, 68, 62, 55];

    this.retentionChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: days,
        datasets: [
          {
            label: 'Baseline (SM-2)',
            data: baselineRetention,
            borderColor: '#888888',
            backgroundColor: 'rgba(136, 136, 136, 0.1)',
            tension: 0.4,
            fill: true
          },
          {
            label: 'ML-Enhanced',
            data: mlRetention,
            borderColor: '#00ff41',
            backgroundColor: 'rgba(0, 255, 65, 0.1)',
            tension: 0.4,
            fill: true
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: { color: '#fff' }
          },
          title: {
            display: true,
            text: 'Retention Curve Comparison',
            color: '#00ff41',
            font: { size: 18 }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            ticks: {
              color: '#fff',
              callback: (value) => value + '%'
            },
            grid: { color: 'rgba(255, 255, 255, 0.1)' },
            title: {
              display: true,
              text: 'Retention Rate (%)',
              color: '#fff'
            }
          },
          x: {
            ticks: { color: '#fff' },
            grid: { color: 'rgba(255, 255, 255, 0.1)' },
            title: {
              display: true,
              text: 'Days Since Review',
              color: '#fff'
            }
          }
        }
      }
    });
  }

  createIntervalChart() {
    const { progress } = this.state;
    if (!progress || !this.intervalChartRef.current) return;

    const ctx = this.intervalChartRef.current.getContext('2d');

    if (this.intervalChart) {
      this.intervalChart.destroy();
    }

    // Extract interval data from progress
    const cards = progress.cards || [];
    const intervals = cards.map(card => card.memoryStrength || 0);

    // Create histogram bins
    const bins = [0, 1, 3, 7, 14, 30, 60];
    const counts = bins.slice(0, -1).map((bin, i) => {
      return intervals.filter(interval => interval >= bin && interval < bins[i + 1]).length;
    });

    this.intervalChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['0-1d', '1-3d', '3-7d', '7-14d', '14-30d', '30-60d'],
        datasets: [
          {
            label: 'Number of Cards',
            data: counts,
            backgroundColor: 'rgba(0, 255, 65, 0.7)',
            borderColor: '#00ff41',
            borderWidth: 2
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: { color: '#fff' }
          },
          title: {
            display: true,
            text: 'Interval Distribution',
            color: '#00ff41',
            font: { size: 18 }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              color: '#fff',
              stepSize: 1
            },
            grid: { color: 'rgba(255, 255, 255, 0.1)' },
            title: {
              display: true,
              text: 'Card Count',
              color: '#fff'
            }
          },
          x: {
            ticks: { color: '#fff' },
            grid: { color: 'rgba(255, 255, 255, 0.1)' },
            title: {
              display: true,
              text: 'Review Interval',
              color: '#fff'
            }
          }
        }
      }
    });
  }

  render() {
    if (!this.props.loggedIn) {
      return <Navigate to="/" replace />;
    }

    const { comparison, progress, loading, error } = this.state;

    return (
      <div className={styles.statsDashboard}>
        <div className={styles.statsContainer}>
          <h1>📊 Performance Analytics</h1>

          {/* Navigation */}
          <NavBar />

          {loading && (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Loading statistics...</p>
            </div>
          )}

          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {!loading && comparison && progress && (
            <>
              {/* Summary Cards */}
              <div className={styles.summaryGrid}>
                <div className={`${styles.summaryCard} ${styles.improvement}`}>
                  <div className={styles.summaryLabel}>Model Test Accuracy</div>
                  <div className={styles.summaryValue}>
                    87.7%
                  </div>
                  <div className={styles.summaryDescription}>
                    Offline evaluation (6.07 vs 49.31 days MAE)
                  </div>
                </div>

                <div className={`${styles.summaryCard} ${styles.totalReviews}`}>
                  <div className={styles.summaryLabel}>Total Reviews</div>
                  <div className={styles.summaryValue}>
                    {progress.totalReviews || 0}
                  </div>
                  <div className={styles.summaryDescription}>
                    Across all cards
                  </div>
                </div>

                <div className={`${styles.summaryCard} ${styles.successRate}`}>
                  <div className={styles.summaryLabel}>Success Rate</div>
                  <div className={styles.summaryValue}>
                    {((progress.successRate || 0) * 100).toFixed(0)}%
                  </div>
                  <div className={styles.summaryDescription}>
                    Overall accuracy
                  </div>
                </div>

                <div className={`${styles.summaryCard} ${styles.activeCards}`}>
                  <div className={styles.summaryLabel}>Active Cards</div>
                  <div className={styles.summaryValue}>
                    {progress.cards?.length || 0}
                  </div>
                  <div className={styles.summaryDescription}>
                    In learning queue
                  </div>
                </div>
              </div>

              {/* Algorithm Comparison Table */}
              <div className={styles.comparisonTableContainer}>
                <h2>Production Algorithm Comparison</h2>
                <p style={{color: '#888', marginBottom: '1rem', fontSize: '0.9rem'}}>
                  {comparison.ml?.totalReviews > 0
                    ? `Comparing ${comparison.baseline?.totalReviews || 0} baseline vs ${comparison.ml?.totalReviews || 0} ML predictions in production`
                    : '⚠️ No ML predictions yet - use the app to generate ML prediction data'}
                </p>
                <table className={styles.comparisonTable}>
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Baseline (SM-2)</th>
                      <th>ML-Enhanced</th>
                      <th>Improvement</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Mean Absolute Error</td>
                      <td>{(comparison.baseline?.mae || comparison.baselineMAE || 0).toFixed(4)} days</td>
                      <td className={styles.mlValue}>{(comparison.ml?.mae || comparison.mlMAE || 0).toFixed(4)} days</td>
                      <td className={styles.improvementValue}>
                        {maeImprovement(comparison)}
                      </td>
                    </tr>
                    <tr>
                      <td>Predictions Made</td>
                      <td>{comparison.baseline?.totalReviews || 0}</td>
                      <td className={styles.mlValue}>{comparison.ml?.totalReviews || 0}</td>
                      <td>-</td>
                    </tr>
                    <tr>
                      <td>Features Used</td>
                      <td>3 (basic)</td>
                      <td className={styles.mlValue}>24 (v2, outcome-aware)</td>
                      <td>+1,600%</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Charts */}
              <div className={styles.chartsGrid}>
                <div className={styles.chartContainer}>
                  <canvas ref={this.comparisonChartRef}></canvas>
                </div>

                <div className={styles.chartContainer}>
                  <canvas ref={this.retentionChartRef}></canvas>
                </div>

                <div className={`${styles.chartContainer} ${styles.wide}`}>
                  <canvas ref={this.intervalChartRef}></canvas>
                </div>
              </div>

              {/* Card Status Overview */}
              <div className={styles.cardStatusContainer}>
                <h2>Card Status Overview</h2>
                <div className={styles.cardsList}>
                  {progress.cards && progress.cards.map((card, index) => (
                    <div key={index} className={styles.cardItem}>
                      <div className={styles.cardQuestion}>{card.question || `Card ${index + 1}`}</div>
                      <div className={styles.cardStats}>
                        <span className={styles.cardStat}>
                          <label>Reviews:</label> {card.totalReviews || 0}
                        </span>
                        <span className={styles.cardStat}>
                          <label>Success:</label> {((card.successRate || 0) * 100).toFixed(0)}%
                        </span>
                        <span className={styles.cardStat}>
                          <label>Interval:</label> {card.memoryStrength || 1} days
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  loggedIn: state.auth.currentUser !== null
});

export default connect(mapStateToProps)(StatsDashboard);
